import { useEffect, useState } from 'react';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Autocomplete,
  Box
} from '@mui/material';
import { PageLayout } from '../../PageLayout';
import { PageCard } from '../../Compounds/CardWithTitle';
import {
  AdviseCustomer,
  AdviseCustomerStatus,
  AdviseCustomerIndustry,
  AdviseCompanySize,
  AdviseBudgetRange
} from '../../../types/advise';
import { PortalUserData } from '../../../types';
import {
  getAdviseActiveCustomerSubject,
  getAdviseCustomerLoadingSubject,
  useAdviseCustomers
} from '../../../store/AdviseCustomer';
import { getSelectedAccountsSubject, getAccountScope, hasAdviseFull } from '../../../store/User';
import { fetchPortalUsers } from '../../../Api/Admin/PortalUsers/fetchPortalUsers';
import { AdviseSubNav } from '../../Compounds/Advise/AdviseSubNav';
import { AdviseAccountGuard } from '../../Compounds/Advise/AdviseAccountGuard';

const STATUS_OPTIONS = Object.values(AdviseCustomerStatus).map((v) => ({
  value: v,
  label: v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}));

const INDUSTRY_OPTIONS = Object.values(AdviseCustomerIndustry).map((v) => ({ value: v, label: v }));
const SIZE_OPTIONS = Object.values(AdviseCompanySize).map((v) => ({ value: v, label: `${v} employees` }));
const BUDGET_OPTIONS = Object.values(AdviseBudgetRange).map((v) => ({ value: v, label: v }));

const TIMELINE_OPTIONS = [
  { value: 'immediate', label: 'Immediate' },
  { value: '1-3_months', label: '1-3 Months' },
  { value: '3-6_months', label: '3-6 Months' },
  { value: '6-12_months', label: '6-12 Months' },
  { value: 'over_12_months', label: 'Over 12 Months' }
];

export const AdviseCustomersPage = () => {
  const [customer, setCustomer] = useState<AdviseCustomer | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Partial<AdviseCustomer>>({});
  const [accountUsers, setAccountUsers] = useState<PortalUserData[]>([]);

  const { ensureCustomerForAccount, updateCustomer } = useAdviseCustomers();
  const canEdit = hasAdviseFull();

  const loadCustomer = async () => {
    const accounts = getAccountScope();
    if (accounts?.length === 1) {
      ensureCustomerForAccount(accounts[0].id, accounts[0].name);
      const { data } = await fetchPortalUsers(accounts[0].id);
      if (data) setAccountUsers(data);
    }
  };

  useEffect(() => {
    const customerSub = getAdviseActiveCustomerSubject().subscribe((c) => {
      setCustomer(c);
      if (c) setForm({ ...c });
    });
    const loadingSub = getAdviseCustomerLoadingSubject().subscribe(setLoading);

    loadCustomer();

    const accountSub = getSelectedAccountsSubject().subscribe(() => loadCustomer());

    return () => {
      customerSub.unsubscribe();
      loadingSub.unsubscribe();
      accountSub.unsubscribe();
    };
  }, []);

  const updateField = (field: string, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!customer) return;
    setSaving(true);
    await updateCustomer(customer.id, form);
    setSaving(false);
  };

  if (loading) {
    return (
      <PageLayout
        pageTitle="Customer Profile"
        dataCy="advise-customers-page">
        <AdviseSubNav />
        <Box className="flex justify-center p-12">
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle="Customer Profile"
      dataCy="advise-customers-page">
      <AdviseSubNav />
      <AdviseAccountGuard>
        {customer ? (
          <PageCard title={customer.company_name}>
            <Box className="flex flex-col gap-4 max-w-3xl">
              <Box className="text-sm font-medium text-gray-600 mb-1">Company Information</Box>
              <TextField
                label="Company Name"
                size="small"
                value={form.company_name || ''}
                disabled
                helperText="Company name is derived from your Salesforce account"
              />
              <Box className="flex gap-4">
                <FormControl
                  size="small"
                  className="flex-1"
                  disabled={!canEdit}>
                  <InputLabel>Industry</InputLabel>
                  <Select
                    label="Industry"
                    value={form.industry || ''}
                    onChange={(e) => updateField('industry', e.target.value)}>
                    {INDUSTRY_OPTIONS.map((opt) => (
                      <MenuItem
                        key={opt.value}
                        value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl
                  size="small"
                  className="flex-1"
                  disabled={!canEdit}>
                  <InputLabel>Company Size</InputLabel>
                  <Select
                    label="Company Size"
                    value={form.company_size || ''}
                    onChange={(e) => updateField('company_size', e.target.value)}>
                    {SIZE_OPTIONS.map((opt) => (
                      <MenuItem
                        key={opt.value}
                        value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <TextField
                label="Company Address"
                size="small"
                value={form.company_address || ''}
                onChange={(e) => updateField('company_address', e.target.value)}
                disabled={!canEdit}
              />

              <Box className="border-t pt-3 mt-1">
                <Box className="text-sm font-medium text-gray-600 mb-2">Contact Information</Box>
              </Box>
              <Box className="flex gap-4">
                <Autocomplete
                  freeSolo
                  className="flex-1"
                  options={accountUsers}
                  getOptionLabel={(opt) =>
                    typeof opt === 'string' ? opt : `${opt.first_name} ${opt.last_name}`.trim()
                  }
                  value={form.contact_person || ''}
                  onChange={(_, val) => {
                    if (typeof val === 'string') {
                      updateField('contact_person', val);
                    } else if (val) {
                      updateField('contact_person', `${val.first_name} ${val.last_name}`.trim());
                      updateField('email', val.email || '');
                    } else {
                      updateField('contact_person', '');
                    }
                  }}
                  onInputChange={(_, val, reason) => {
                    if (reason === 'input') updateField('contact_person', val);
                  }}
                  disabled={!canEdit}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Contact Person"
                      size="small"
                      helperText="Select a portal user or type a name"
                    />
                  )}
                />
                <TextField
                  label="Email"
                  size="small"
                  className="flex-1"
                  type="email"
                  value={form.email || ''}
                  onChange={(e) => updateField('email', e.target.value)}
                  disabled={!canEdit}
                />
                <TextField
                  label="Phone"
                  size="small"
                  className="flex-1"
                  value={form.phone || ''}
                  onChange={(e) => updateField('phone', e.target.value)}
                  disabled={!canEdit}
                />
              </Box>
              <FormControl
                size="small"
                disabled={!canEdit}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={form.status || ''}
                  onChange={(e) => updateField('status', e.target.value)}>
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem
                      key={opt.value}
                      value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box className="border-t pt-3 mt-1">
                <Box className="text-sm font-medium text-gray-600 mb-2">Scale & Budget</Box>
              </Box>
              <Box className="flex gap-4">
                <TextField
                  label="Number of Agents"
                  size="small"
                  type="number"
                  className="flex-1"
                  value={form.num_agents || ''}
                  onChange={(e) => updateField('num_agents', Number(e.target.value) || 0)}
                  disabled={!canEdit}
                />
                <FormControl
                  size="small"
                  className="flex-1"
                  disabled={!canEdit}>
                  <InputLabel>Budget Range</InputLabel>
                  <Select
                    label="Budget Range"
                    value={form.budget_range || ''}
                    onChange={(e) => updateField('budget_range', e.target.value)}>
                    {BUDGET_OPTIONS.map((opt) => (
                      <MenuItem
                        key={opt.value}
                        value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl
                  size="small"
                  className="flex-1"
                  disabled={!canEdit}>
                  <InputLabel>Deployment Timeline</InputLabel>
                  <Select
                    label="Deployment Timeline"
                    value={form.timeline || ''}
                    onChange={(e) => updateField('timeline', e.target.value)}>
                    {TIMELINE_OPTIONS.map((opt) => (
                      <MenuItem
                        key={opt.value}
                        value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <TextField
                label="Notes"
                size="small"
                multiline
                rows={3}
                value={form.notes || ''}
                onChange={(e) => updateField('notes', e.target.value)}
                disabled={!canEdit}
              />

              {canEdit && (
                <Box className="flex justify-end mt-2">
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSave}
                    disabled={saving}>
                    {saving ? <CircularProgress size={20} /> : 'Save Changes'}
                  </Button>
                </Box>
              )}
            </Box>
          </PageCard>
        ) : (
          <Box className="text-center py-12 text-gray-400">No customer profile found for this account.</Box>
        )}
      </AdviseAccountGuard>
    </PageLayout>
  );
};
